import { useEffect } from 'react';

import type { SoftphoneStatus } from './use-whatsapp-call';

/**
 * How long "answer this call when it rings here" is kept. A ring arrives in a
 * second or two; past this the call is long over, and a late re-delivery of
 * the same call (webhook retry) must not be answered out of the blue.
 */
export const AUTO_ANSWER_MAX_WAIT_MS = 60_000;

/**
 * The notification's "Answer" button: once THAT call is ringing here, answer
 * it - exactly as if Accept were tapped. Waits while the softphone is idle
 * (the ring is still on its way); gives up the moment it is busy with
 * something else, so it can never answer a different call - and gives up
 * after AUTO_ANSWER_MAX_WAIT_MS if that call never rings here.
 */
export const useAutoAnswer = (
  target: string | null,
  status: SoftphoneStatus,
  ringingCallId: string | null,
  answer: () => unknown,
  clear: () => void,
): void => {
  useEffect(() => {
    if (!target) return;
    if (status === 'incoming' && ringingCallId === target) {
      clear();
      void answer();
      return;
    }
    if (status !== 'idle' && status !== 'incoming') clear();
  }, [target, status, ringingCallId, answer, clear]);

  useEffect(() => {
    if (!target) return;
    const timer = window.setTimeout(clear, AUTO_ANSWER_MAX_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [target, clear]);
};
